import type { RakutenCoupon } from '../api/rakutenCoupons';
import { formatCouponEndAt, formatDeadlineHint } from '../lib/date';
import { safeImageUrl, safeRakutenCouponUrl } from '../lib/url';

export interface DiscountPresentation {
  value: string;
  suffix: string | null;
  kind: 'percentage' | 'fixed_amount' | 'free_shipping' | 'unknown';
}

export function formatDiscount(coupon: RakutenCoupon): DiscountPresentation {
  if (coupon.discount_type === 'percentage' && typeof coupon.discount_value === 'number') {
    return { value: new Intl.NumberFormat('ja-JP').format(coupon.discount_value), suffix: '% OFF', kind: 'percentage' };
  }
  if (coupon.discount_type === 'fixed_amount' && typeof coupon.discount_value === 'number') {
    return { value: `${new Intl.NumberFormat('ja-JP').format(coupon.discount_value)}円`, suffix: 'OFF', kind: 'fixed_amount' };
  }
  if (coupon.discount_type === 'free_shipping') {
    return { value: '送料無料', suffix: null, kind: 'free_shipping' };
  }
  return { value: 'クーポン', suffix: null, kind: 'unknown' };
}

function appendTextElement(parent: HTMLElement, tag: keyof HTMLElementTagNameMap, className: string, text: string): HTMLElement {
  const element = document.createElement(tag);
  element.className = className;
  element.textContent = text;
  parent.append(element);
  return element;
}

function createCouponImage(coupon: RakutenCoupon): HTMLElement | null {
  const imageUrl = safeImageUrl(coupon.image_url);
  if (!imageUrl) return null;
  const figure = document.createElement('figure');
  figure.className = 'coupon-card__visual';
  const image = document.createElement('img');
  image.className = 'coupon-card__image';
  image.src = imageUrl;
  image.alt = '';
  image.loading = 'lazy';
  image.decoding = 'async';
  image.addEventListener('error', () => figure.remove(), { once: true });
  figure.append(image);
  return figure;
}

function createCouponCta(destinationUrl: string): HTMLAnchorElement | null {
  const safeUrl = safeRakutenCouponUrl(destinationUrl);
  if (!safeUrl) return null;
  const link = document.createElement('a');
  link.className = 'coupon-card__cta';
  link.href = safeUrl;
  link.textContent = 'クーポンを獲得する';
  link.addEventListener('click', (event) => {
    if (link.dataset.opening === 'true') {
      event.preventDefault();
      return;
    }
    link.dataset.opening = 'true';
    link.setAttribute('aria-disabled', 'true');
    link.classList.add('coupon-card__cta--opening');
  });
  return link;
}

export function createCouponCard(coupon: RakutenCoupon, now: Date = new Date()): HTMLElement {
  const article = document.createElement('article');
  article.className = 'coupon-card';
  article.dataset.couponId = String(coupon.id);

  const image = createCouponImage(coupon);
  if (image) article.append(image);

  const body = document.createElement('div');
  body.className = 'coupon-card__body';
  appendTextElement(body, 'p', 'coupon-card__eyebrow', 'RAKUTEN COUPON');

  const discount = formatDiscount(coupon);
  const discountRow = document.createElement('div');
  discountRow.className = `coupon-card__discount coupon-card__discount--${discount.kind}`;
  appendTextElement(discountRow, 'span', 'coupon-card__discount-value', discount.value);
  if (discount.suffix) appendTextElement(discountRow, 'span', 'coupon-card__discount-suffix', discount.suffix);
  body.append(discountRow);

  if (coupon.title.trim()) appendTextElement(body, 'h2', 'coupon-card__title', coupon.title);
  if (coupon.usage_conditions.trim()) {
    const conditions = document.createElement('div');
    conditions.className = 'coupon-card__conditions';
    appendTextElement(conditions, 'span', 'coupon-card__conditions-label', '利用条件');
    appendTextElement(conditions, 'p', 'coupon-card__conditions-text', coupon.usage_conditions);
    body.append(conditions);
  }
  if (coupon.description.trim()) appendTextElement(body, 'p', 'coupon-card__description', coupon.description);

  const deadline = document.createElement('div');
  deadline.className = 'coupon-card__deadline';
  const deadlineHint = formatDeadlineHint(coupon.end_at, now);
  if (deadlineHint) appendTextElement(deadline, 'span', 'coupon-card__deadline-hint', deadlineHint);
  appendTextElement(deadline, 'time', 'coupon-card__deadline-time', formatCouponEndAt(coupon.end_at));
  body.append(deadline);

  const cta = createCouponCta(coupon.destination_url);
  if (cta) body.append(cta);
  else appendTextElement(body, 'p', 'coupon-card__cta-unavailable', '楽天ページへのリンクを確認できません');

  article.append(body);
  return article;
}
